// frontend/src/pages/admin/NotificationForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Button, DatePicker, Switch } from 'antd';
import api from '../../utils/api';
import dayjs from 'dayjs';
import { useNotificationApi } from '../../context/NotificationContext';

const { TextArea } = Input;

const NotificationForm = ({ visible, onCancel, onSuccess, notification }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const notificationApi = useNotificationApi();

  // Notification helpers (mirroring TicketTypeForm.js pattern)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((messageContent, key = 'globalLoading') => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Effect to populate form fields
  useEffect(() => {
    if (visible) {
      setLoading(false);
      if (notification) {
        form.setFieldsValue({
          title: notification.title,
          message: notification.message,
          expiresAt: notification.expiresAt ? dayjs(notification.expiresAt) : null,
          isActive: notification.isActive,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          isActive: true
        });
      }
    }
  }, [visible, notification, form]);

  const handleSubmit = async () => {
    let hideLoading;
    try {
      setLoading(true);
      hideLoading = showLoading(
        notification ? 'Updating notification...' : 'Creating notification...',
        'notificationFormLoading'
      );

      const values = await form.validateFields();

      const payload = {
        title: values.title,
        message: values.message,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
        isActive: values.isActive,
      };

      if (notification) {
        await api.put(`/api/notifications/global/${notification.id}`, payload);
        showSuccess('Notification Updated', 'Notification updated successfully!', 'notificationUpdateSuccess');
      } else {
        await api.post('/api/notifications/global', payload);
        showSuccess('Notification Created', 'New notification created successfully!', 'notificationCreateSuccess');
      }

      setTimeout(() => {
        if (hideLoading && typeof hideLoading === 'function') hideLoading();
        onSuccess(true);
        onCancel();
      }, 500);

    } catch (error) {
      if (hideLoading && typeof hideLoading === 'function') hideLoading();
      setLoading(false);
      console.error('NotificationForm: Form submission error:', error.response?.data || error.message, error);
      showError(
        'Submission Failed', 
        error.response?.data?.message || 'Failed to save notification.',
        'notificationFormSubmitError'
      );
    }
  };

  return (
    <Modal
      open={visible}
      title={notification ? 'Edit Notification' : 'Add New Notification'}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={700}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        name="notification_form"
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[
            { required: true, message: 'Please input the notification title!' },
            { max: 255, message: 'Title must be 255 characters or less' }
          ]}
        >
          <Input placeholder="Enter notification title" />
        </Form.Item>

        <Form.Item
          name="message"
          label="Message"
          rules={[
            { required: true, message: 'Please input the notification message!' }
          ]}
        >
          <TextArea rows={4} placeholder="Enter notification message" />
        </Form.Item>

        <Form.Item
          name="expiresAt"
          label="Expiration Date (optional)"
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: '100%' }}
            placeholder="Select expiration date"
          />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Status"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NotificationForm;